import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeviceStatusCard = ({ 
  device, 
  onLockDevice,
  onUnlockDevice,
  onDeviceClick,
  isControlling = false,
  className = "" 
}) => {
  const getStatusColor = () => {
    if (!device?.isOnline) return 'text-muted-foreground';
    return device?.isLocked ? 'text-error' : 'text-success';
  };

  const getStatusIcon = () => {
    if (!device?.isOnline) return 'WifiOff';
    return device?.isLocked ? 'Lock' : 'Unlock';
  };

  const getStatusText = () => {
    if (!device?.isOnline) return 'Offline';
    return device?.isLocked ? 'Locked' : 'Unlocked';
  };

  const getBatteryIcon = () => {
    if (device?.batteryLevel > 75) return 'Battery';
    if (device?.batteryLevel > 50) return 'Battery';
    if (device?.batteryLevel > 25) return 'Battery';
    return 'BatteryLow';
  };

  const getBatteryColor = () => {
    if (device?.batteryLevel > 50) return 'text-success';
    if (device?.batteryLevel > 25) return 'text-warning';
    return 'text-error';
  };

  const formatLastSeen = (timestamp) => {
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diff = now - lastSeen;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div 
      className={`
        bg-card border border-border rounded-lg p-4 security-shadow-card security-transition
        hover:shadow-lg cursor-pointer
        ${!device?.isOnline ? 'opacity-75' : ''}
        ${className}
      `}
      onClick={() => onDeviceClick(device)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`
            w-10 h-10 rounded-lg flex items-center justify-center
            ${device?.isOnline ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}
          `}>
            <Icon name="DoorOpen" size={20} />
          </div>
          <div>
            <h4 className="font-medium text-foreground">{device?.name}</h4>
            <p className="text-sm text-muted-foreground">{device?.location}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className={`
            w-2 h-2 rounded-full
            ${device?.isOnline ? 'bg-success security-pulse' : 'bg-error'}
          `} />
          <span className={`text-xs font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Icon name={getBatteryIcon()} size={16} className={getBatteryColor()} />
            <span className={`text-sm ${getBatteryColor()}`}>
              {device?.batteryLevel}%
            </span>
          </div>
          
          <div className="flex items-center space-x-1 text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span className="text-xs">
              {formatLastSeen(device?.lastSeen)}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <Icon name={getStatusIcon()} size={16} className={getStatusColor()} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          ID: {device?.id}
        </div>
        
        {/* Lock/Unlock Button */}
        <div onClick={(e) => e.stopPropagation()}>
          {device?.isLocked ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnlockDevice(device?.device_id)}
              disabled={!device?.isOnline || isControlling}
              loading={isControlling}
              iconName="Unlock"
              className="text-xs bg-green-600/20 text-green-400 hover:bg-green-600/30 border-green-600/30"
            >
              {isControlling ? 'Unlocking...' : 'Unlock'}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={() => onLockDevice(device?.device_id)}
              disabled={!device?.isOnline || isControlling}
              loading={isControlling}
              iconName="Lock"
              className="text-xs"
            >
              {isControlling ? 'Locking...' : 'Lock'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceStatusCard;